import type { StockAuditRow, StockReconRow, CommitDriftRow, InventoryChange, DiscrepancyRow } from './types';

/**
 * #209 Discrepancias fetchers. /api/stock-audit reads the paceBI-owned SQLite
 * worklist; /api/stock-recon is the read-only backstop (daemon-backed). 60s
 * timeout matches the other daemon-adjacent drill-downs.
 */
const FETCH_TIMEOUT_MS = 60_000;

function isMock(): boolean {
  return import.meta.env.VITE_USE_MOCKS === '1';
}

async function getJSON<T>(url: string): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const res = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
    if (res.status === 401) throw new Error('unauthenticated');
    if (res.status === 403) throw new Error('forbidden');
    if (res.status === 502 || res.status === 503) {
      throw new Error('Servicio de auditoría no disponible. Intenta de nuevo en unos minutos.');
    }
    if (!res.ok) throw new Error(`${url} failed: ${res.status}`);
    return (await res.json()) as T;
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new Error(`${url} timed out after ${FETCH_TIMEOUT_MS}ms`);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

export interface StockAuditQuery {
  sid?: number;
  from?: string;
  to?: string;
  cause?: string;
  slid?: string;
  tkt_id?: number;
  miss?: boolean;
}

export async function fetchStockAudit(q: StockAuditQuery = {}): Promise<StockAuditRow[]> {
  if (isMock()) return [];
  const p = new URLSearchParams();
  if (q.sid != null && q.sid > 0) p.set('sid', String(q.sid));
  if (q.from) p.set('from', q.from);
  if (q.to) p.set('to', q.to);
  if (q.cause) p.set('cause', q.cause);
  if (q.slid) p.set('slid', q.slid);
  if (q.tkt_id != null && q.tkt_id > 0) p.set('tkt_id', String(q.tkt_id));
  if (q.miss) p.set('miss', '1');
  return getJSON<StockAuditRow[]>(`/api/stock-audit?${p.toString()}`);
}

export async function fetchStockRecon(sid: number, date: string): Promise<StockReconRow[]> {
  if (isMock()) return [];
  const p = new URLSearchParams({ sid: String(sid), date });
  return getJSON<StockReconRow[]>(`/api/stock-recon?${p.toString()}`);
}

/** #294 committed-drift. Response is { drifts: [...] }; returns the array. */
export async function fetchCommitDrift(sid = 0): Promise<CommitDriftRow[]> {
  if (isMock()) return [];
  const p = new URLSearchParams();
  if (sid > 0) p.set('sid', String(sid));
  const r = await getJSON<{ drifts: CommitDriftRow[] }>(`/api/commit-drift?${p.toString()}`);
  return r.drifts ?? [];
}

export interface InventoryChangelogQuery {
  sid?: number;
  from?: string;
  to?: string;
  line?: string;
  part?: string;
}

/**
 * On-hand change log ("Movimientos de existencia") — the `inv` WRITE trigger
 * capture. Response is a plain array, same envelope as /api/stock-audit (not
 * wrapped like /api/commit-drift's {drifts:[...]}). See
 * docs/superpowers/specs/2026-08-25-inventory-changelog-trigger-design.md.
 */
export async function fetchInventoryChangelog(q: InventoryChangelogQuery = {}): Promise<InventoryChange[]> {
  if (isMock()) return [];
  const p = new URLSearchParams();
  if (q.from) p.set('from', q.from);
  if (q.to) p.set('to', q.to);
  if (q.sid != null && q.sid > 0) p.set('sid', String(q.sid));
  if (q.line) p.set('line', q.line);
  if (q.part) p.set('part', q.part);
  return getJSON<InventoryChange[]>(`/api/inventory-changelog?${p.toString()}`);
}

/**
 * Task 11 ("Discrepancias: Detección" plan) — reconciled discrepancy
 * residuals ("Detectadas"). Response is wrapped ({ rows: [...] }), same
 * envelope shape as /api/commit-drift's {drifts:[...]}; returns the array.
 */
export async function fetchDiscrepancies(sid?: number): Promise<DiscrepancyRow[]> {
  if (isMock()) return [];
  const p = new URLSearchParams();
  if (sid != null && sid > 0) p.set('sid', String(sid));
  const r = await getJSON<{ rows: DiscrepancyRow[] }>(`/api/discrepancies?${p.toString()}`);
  return r.rows ?? [];
}
